import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import PetchemRunningDaily from '@/components/polymer/PetchemRunningDaily';
import PetchemAverages from '@/components/polymer/PetchemAverages';
import { dataService } from '@rti/shared';
import PolymerDashboardLayout from '@/components/polymer/PolymerDashboardLayout';
import type { PolymerDashboardConfig } from '@/types/polymer-dashboard.types';

const PETCHEM_CONFIG: PolymerDashboardConfig = {
  polymer: 'PC',
  polymerFullName: 'PetChem',
  defaultTab: 'overview',
  tabs: [
    { key: 'overview', label: 'Overview' },
    { key: 'running-daily', label: 'Running Daily' },
    { key: 'averages', label: 'Averages' },
  ],
};

const ChartPanel: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => (
  <div className={`bg-rs-off-black/60 backdrop-blur-sm border border-rs-jet-light/50 rounded-xl shadow-lg shadow-black/20 p-5 ${className || ''}`}>
    <h3 className="text-xs font-medium text-rs-off-white/70 uppercase tracking-wider mb-4">{title}</h3>
    {children}
  </div>
);

const PetChemDashboard: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [petchemData, setPetchemData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);
  const [isSmallOverview, setIsSmallOverview] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 750px)');
    setIsSmallOverview(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsSmallOverview(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (hasFetched.current || !isAuthenticated) return;
    hasFetched.current = true;
    setLoading(true);
    dataService.getPetchemData()
      .then((data) => setPetchemData(Array.isArray(data) ? data : []))
      .catch(() => setPetchemData([]))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const renderContent = (activeTab: string) => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {isSmallOverview ? (
              <PetchemRunningDaily petchemData={petchemData} />
            ) : (
              <ChartPanel title="Running Daily">
                <PetchemRunningDaily petchemData={petchemData} />
              </ChartPanel>
            )}
            {isSmallOverview ? (
              <PetchemAverages petchemData={petchemData} showDetails={false} />
            ) : (
              <ChartPanel title="Averages">
                <PetchemAverages petchemData={petchemData} showDetails={false} />
              </ChartPanel>
            )}
          </div>
        );
      case 'running-daily':
        return <PetchemRunningDaily petchemData={petchemData} />;
      case 'averages':
        return <PetchemAverages petchemData={petchemData} />;
      default:
        return null;
    }
  };

  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-rs-black">
        <Loader2 className="animate-spin opacity-30 w-16 h-16 text-rs-green" />
      </div>
    );
  }

  return (
    <PolymerDashboardLayout config={PETCHEM_CONFIG} renderContent={renderContent} />
  );
};

export default PetChemDashboard;
